import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Sparkles, Loader as LoaderIcon, Trophy, Archive, Images, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { STATUS_COLORS } from "@/data/content";

function Stat({ icon: Icon, label, value, testid }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-hive-card p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-slate-400">
                <Icon className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
            </div>
            <p className="mt-2 font-display text-3xl font-bold text-white">{value}</p>
        </div>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    if (!stats) {
        return <div className="flex justify-center py-24"><LoaderIcon className="h-8 w-8 animate-spin text-violet-400" /></div>;
    }

    const s = stats.by_status;
    const inProgress = (s["Contacted"] || 0) + (s["Qualified"] || 0) + (s["Proposal Sent"] || 0);

    return (
        <div data-testid="admin-dashboard-home">
            <h1 className="font-display text-2xl font-bold text-white">Dashboard</h1>
            <p className="mt-1 text-sm text-slate-400">Real inquiry data from your website. {stats.analytics_note}</p>

            <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <Stat icon={Inbox} label="Total Inquiries" value={stats.inquiries_total} testid="stat-total-inquiries" />
                <Stat icon={Sparkles} label="New" value={s["New"] || 0} testid="stat-new-inquiries" />
                <Stat icon={LoaderIcon} label="In Progress" value={inProgress} testid="stat-in-progress" />
                <Stat icon={Trophy} label="Qualified / Won" value={`${s["Qualified"] || 0} / ${s["Won"] || 0}`} testid="stat-qualified-won" />
                <Stat icon={Archive} label="Archived" value={s["Archived"] || 0} testid="stat-archived" />
                <Stat icon={Images} label="Portfolio Projects" value={`${stats.portfolio_published}/${stats.portfolio_total}`} testid="stat-portfolio" />
                <Stat icon={Eye} label="Pageviews (30d)" value={stats.pageviews_30d} testid="stat-pageviews-30d" />
                <Stat icon={Eye} label="Pageviews (All)" value={stats.pageviews_total} testid="stat-pageviews-total" />
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-hive-card" data-testid="recent-inquiries-panel">
                    <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                        <h2 className="font-display font-bold text-white">Recent Inquiries</h2>
                        <Link to="/admin/leads" data-testid="view-all-inquiries-link" className="text-xs font-semibold text-violet-300 hover:text-violet-200">View all →</Link>
                    </div>
                    {stats.recent.length === 0 ? (
                        <p className="px-6 py-10 text-center text-sm text-slate-500" data-testid="recent-inquiries-empty">No inquiries yet. They will appear here as soon as the first form submission arrives.</p>
                    ) : (
                        <ul className="divide-y divide-white/5">
                            {stats.recent.map((r) => (
                                <li key={r.id}>
                                    <Link to={`/admin/leads/${r.id}`} data-testid={`recent-inquiry-${r.reference}`} className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-white/5 transition-colors">
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-white">{r.full_name} <span className="font-normal text-slate-500">· {r.service}</span></p>
                                            <p className="truncate text-xs text-slate-500">{r.reference} — {new Date(r.created_at).toLocaleString()}</p>
                                        </div>
                                        <span className={`shrink-0 rounded-full border px-3 py-1 text-[11px] font-semibold ${STATUS_COLORS[r.status] || ""}`}>{r.status}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="rounded-2xl border border-white/10 bg-hive-card" data-testid="top-pages-panel">
                    <div className="border-b border-white/10 px-6 py-4">
                        <h2 className="font-display font-bold text-white">Top Pages</h2>
                        <p className="mt-0.5 text-[11px] text-slate-500">Built-in, privacy-friendly counts — page paths only.</p>
                    </div>
                    {stats.top_pages.length === 0 ? (
                        <p className="px-6 py-10 text-center text-sm text-slate-500">No traffic recorded yet.</p>
                    ) : (
                        <ul className="divide-y divide-white/5">
                            {stats.top_pages.map((p) => (
                                <li key={p.path} className="flex items-center justify-between px-6 py-3.5 text-sm">
                                    <span className="truncate font-mono2 text-xs text-slate-300">{p.path}</span>
                                    <span className="ml-3 shrink-0 font-semibold text-violet-300">{p.views}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
